// Step-up authentication (contract §4.1) — the "confirm your identity to continue" step
// embedded INSIDE a money-moving confirmation dialog, never a second modal stack.
//
// Reuses the repo's existing Google Identity Services code-client flow — the same one
// `lib/GoogleLoginButton.jsx` drives for sign-in (see that file's header for why the code
// flow + `select_account` replaced the ID-token flow: only the code client can force
// Google's account chooser instead of silently reusing the browser's last account).
//
// Step-up does NOT mint a session. The authorization code is exchanged, server-side, for
// a short-lived single-use step-up secret at `POST /gateway/reauth` (§4.1) — a different
// mint than `/auth/session`, verified through the same `id_token.verify_oauth2_token`
// path. `onVerified(token)` fires once that mint succeeds; the caller attaches the token
// as `X-Stepup-Token` on the ONE retry that follows and then discards it (the token is
// single-use server-side regardless of what the frontend does with it).
//
// WHAT IS SENT IS `{ code }`, NOT `{ credential }`. `initCodeClient` hands back an
// authorization CODE; `credential` is the name of an ID TOKEN, which is what the OTHER
// Google client (`initTokenClient` / One Tap) produces. Sending the code under the
// `credential` name made the backend feed it to `verify_oauth2_token` — a JWT parser —
// which raised on every real operator, so all six money-moving endpoints answered 401
// REAUTH_REQUIRED for ever. The mint accepts either shape by name, exactly as
// `/auth/session` does; the name is what says which one this is.
import React, { useEffect, useRef, useState } from "react";
import defaultGatewayApi from "../api";
import { extractGatewayErrorCode } from "../errors";
import { gatewayErrorText } from "../errorText";
import { useT } from "../../lib/LanguageContext";
import { Button } from "../../components/ui/button";
import { Loader2, ShieldCheck } from "lucide-react";

const GIS_SRC = "https://accounts.google.com/gsi/client";
const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const SCOPE = "openid email profile";

let gisScriptPromise = null;
function loadGisScript() {
  if (gisScriptPromise) return gisScriptPromise;
  gisScriptPromise = new Promise((resolve, reject) => {
    if (window.google?.accounts?.oauth2) {
      resolve();
      return;
    }
    const existing = document.querySelector(`script[src="${GIS_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("gis_load_failed")));
      return;
    }
    const el = document.createElement("script");
    el.src = GIS_SRC;
    el.async = true;
    el.defer = true;
    el.onload = () => resolve();
    el.onerror = () => {
      gisScriptPromise = null; // allow a retry after a transient load failure
      reject(new Error("gis_load_failed"));
    };
    document.body.appendChild(el);
  });
  return gisScriptPromise;
}

/**
 * @param {function} onVerified called with the freshly-minted step-up token (string)
 * @param {object} [gatewayApi] DI seam (contract §9) — defaults to the module singleton
 * @param {string} [promptKey] `common` key of the sentence above the button. The default
 *   names money because most step-up actions move it; a caller whose action does not
 *   (the lifecycle policy) passes its own, so the operator is never told something false.
 */
export default function StepUpAuth({ onVerified, gatewayApi = defaultGatewayApi, promptKey = "gw.stepup.prompt" }) {
  const t = useT("common");
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorCode, setErrorCode] = useState(null);
  const [configMissing, setConfigMissing] = useState(false);
  const codeClientRef = useRef(null);
  const onVerifiedRef = useRef(onVerified);
  onVerifiedRef.current = onVerified;

  useEffect(() => {
    let live = true;
    if (!CLIENT_ID) {
      setConfigMissing(true);
      return undefined;
    }

    const handleCode = async (response) => {
      if (response?.error || !response?.code) {
        setBusy(false);
        setErrorCode("SIGN_IN_FAILED");
        return;
      }
      try {
        const { token } = await gatewayApi.reauth({ code: response.code });
        if (!live) return;
        setBusy(false);
        onVerifiedRef.current?.(token);
      } catch (e) {
        if (!live) return;
        setBusy(false);
        setErrorCode(extractGatewayErrorCode(e) || "REAUTH_REQUIRED");
      }
    };

    loadGisScript()
      .then(() => {
        if (!live || !window.google?.accounts?.oauth2) return;
        codeClientRef.current = window.google.accounts.oauth2.initCodeClient({
          client_id: CLIENT_ID,
          scope: SCOPE,
          ux_mode: "popup",
          select_account: true,
          // Identity only: GIS's default (true) would merge in every scope the account
          // ever granted this OAuth client, Calendar included (see GoogleLoginButton.jsx).
          include_granted_scopes: false,
          callback: handleCode,
          error_callback: (err) => {
            setBusy(false);
            if (err?.type === "popup_closed") return; // deliberate dismissal, not an error
            setErrorCode("SIGN_IN_FAILED");
          },
        });
        setReady(true);
      })
      .catch(() => {
        if (live) setConfigMissing(true);
      });

    return () => {
      live = false;
    };
  }, [gatewayApi]);

  const start = () => {
    if (!codeClientRef.current || busy) return;
    setErrorCode(null);
    setBusy(true);
    codeClientRef.current.requestCode();
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 space-y-2" data-testid="stepup-auth">
      <p className="text-sm text-slate-600 flex items-center gap-1.5">
        <ShieldCheck className="w-4 h-4 text-slate-400 flex-shrink-0" />
        {t(promptKey)}
      </p>
      {configMissing ? (
        <p className="text-xs text-rose-600" role="alert" data-testid="stepup-config-missing">
          {t("googleAuth.configMissing")}
        </p>
      ) : (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={start}
          disabled={!ready || busy}
          data-testid="stepup-start"
        >
          {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
          {busy ? t("gw.stepup.verifying") : t("gw.stepup.confirmIdentity")}
        </Button>
      )}
      {errorCode && (
        <p className="text-xs text-rose-600" role="alert" data-testid="stepup-error">
          {errorCode === "SIGN_IN_FAILED" ? t("googleAuth.signInFailed") : gatewayErrorText(errorCode, t)}
        </p>
      )}
    </div>
  );
}
